import { getId } from '@shared/helpers/object.helpers';
import type { Storey } from '@shared/models/design/storey.model';
import type { ItemOrItemId } from '@shared/models/type-helpers.model';
import {
  getMezzanineStoreysInStorey,
  getStoreyById,
} from '../../storey.helpers';
import { designGuards } from '../../design-typeguards.helpers';

/**
 * Remove one or many storeys by id.
 *
 * Note: If the caller attempts to remove *all* storeys, we keep the last storey and clear its
 * {@link Storey.shapes} instead. This prevents impossible "design with zero storeys" states while still
 * clearing all geometry (similar to how Boverket constraints tend to keep a minimal valid structure).
 */
export const removeStoreysFromList = (
  storeys: Storey[],
  ...storeyOrIds: ItemOrItemId<Storey>[]
): Storey[] => {
  const removableIds = Array.from(
    new Set(storeyOrIds.map((candidate) => getId(candidate))),
  );

  if (!isRemoveStoreysAllowed(storeys, removableIds)) {
    throw new Error('Cannot remove storey while mezzanine storeys remain.');
  }

  const nextStoreys = storeys.filter(
    (storey) => !removableIds.includes(storey.id),
  );

  // No storeys were removed
  if (nextStoreys.length === storeys.length) return storeys;

  // If caller tries to remove *all* storeys, keep a single (last) storey and clear its shapes.
  if (nextStoreys.length === 0) {
    const lastStorey = storeys.at(-1);
    if (!lastStorey) return storeys;

    // Prevent keeping a mezzanine as the only storey, as this violates validation rules
    // (MEZZANINE_BOTTOM_FORBIDDEN and MEZZANINE_PARENT_MISSING)
    if (designGuards.is.mezzanineStorey(lastStorey)) {
      throw new Error(
        'Cannot remove all storeys when the last remaining storey is a mezzanine',
      );
    }

    if (!lastStorey.shapes.length) return [lastStorey];
    return [{ ...lastStorey, shapes: [] }];
  }

  return nextStoreys;
};

/**
 * Validate whether storey removals are allowed without leaving mezzanines orphaned.
 * @param storeys - The storeys to mutate
 * @param removableIds - The storey ids requested for removal
 * @returns True when all removals are permitted
 */
const isRemoveStoreysAllowed = (
  storeys: Storey[],
  removableIds: Storey['id'][],
): boolean => {
  const removableSet = new Set(removableIds);
  return removableIds.every((candidateId) => {
    const storey = getStoreyById(storeys, candidateId);
    if (!storey) return true;
    const mezzanineStoreys = getMezzanineStoreysInStorey(storeys, storey);
    if (!mezzanineStoreys.length) return true;
    // Prevent leaving mezzanines without their parent storey.
    return mezzanineStoreys.every((mezzanine) =>
      removableSet.has(mezzanine.id),
    );
  });
};
